'use client';
import React, { useMemo, useState } from 'react';
import { INoteBook, INotebookStateCreator } from '@cms/services';
import { Text, Note, Icon } from '@ui/components';
import { ContentItemMenu } from './content-item-menu';
import { useParams } from 'next/navigation';
import dayjs from 'dayjs';
import { ContentItemDeleteModal } from './content-delete-modal';
import { PinIcon, Folder } from 'lucide-react';
import { twMerge } from 'tailwind-merge';
import { DynamicRouteBuilder } from '@ui/constants';
import Link from 'next/link';
import { IWhitelabelConfig } from '@ui/components/organisms/user/creator-details.typings';
import { getLabel } from '@ui/lib';

interface IContentItemProps {
  notebook: INoteBook;
  state?: INotebookStateCreator;
  userType?: string;
  isAuthenticated?: boolean;
  isFanOfCreator?: boolean;
  creator_id: string;
  whitelabelConfig?: IWhitelabelConfig;
  creatorDisplayName: string;
}

export const NotebookItem: React.FC<IContentItemProps> = ({
  notebook,
  state,
  userType,
  isAuthenticated,
  isFanOfCreator,
  creator_id,
  whitelabelConfig,
  creatorDisplayName,
}) => {
  const { creatorSlug } = useParams();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [notebookToDelete, setNotebookToDelete] = useState<string | null>(null);

  const handleDelete = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    setNotebookToDelete(notebook?.id || null);
    setDeleteModalOpen(true);
  };

  const handleMove = (e: any) => {
    e.stopPropagation();
    e.preventDefault();
    state?.onMoveNotebook(notebook);
    e?.view?.close();
    setDropdownOpen(false);
  };

  const contentURL = useMemo(() => {
    if (userType === 'creator') {
      return `/dashboard/contents/editor?id=${notebook?.id}`;
    }

    return DynamicRouteBuilder.contentBySlug(
      creatorDisplayName || (creatorSlug as string) || creator_id,
      notebook?.slug || notebook?.id,
    );
  }, [notebook?.id, creator_id, creatorSlug, userType, creatorDisplayName]);

  const dynamicDate = notebook?.date_of_publish ? notebook?.date_of_publish : notebook?.created_at;

  const shouldShowPublishDate = useMemo(() => {
    if (!notebook?.miscellaneous) return true;
    return notebook?.miscellaneous?.showPublishDate ?? true;
  }, [notebook?.miscellaneous]);

  const handlePin = async (e: React.MouseEvent<HTMLDivElement>) => {
    setDropdownOpen(false);
    e.stopPropagation();
    e.preventDefault();
    await state?.handleTogglePin(notebook);
  };

  const shouldShowLockedContentWording = useMemo(() => {
    return isAuthenticated && isFanOfCreator && !notebook.has_access;
  }, [notebook?.has_access, isFanOfCreator, isAuthenticated]);

  return (
    <Link href={contentURL}>
      <div
        className={twMerge(
          'relative w-full h-[300px] rounded-md pb-4 cursor-pointer border border-gray-300',
          notebook?.pinned_at && 'border-primaryActive',
        )}
        key={notebook?.id}>
        <div className="relative bg-primaryLight/10 w-full rounded-t-md">
          {notebook?.pinned_at && (
            <div className="flex items-center absolute top-[-16px] right-4 bg-primaryLight h-8 w-8 rounded-full justify-center z-50">
              <PinIcon className="text-primaryActive font-thin h-4 w-4 fill-white" />
            </div>
          )}
          <div className="relative rounded-t-md overflow-hidden h-[180px] flex justify-center items-center p-">
            {notebook?.visibility === 'tiers' && (
              <div className="absolute top-2 left-2 z-50">
                <div className="backdrop-blur-md bg-white/60 border border-gray-300/40 shadow-sm text-gray-900 text-[10px] md:text-xs font-semibold px-2.5 py-0.5 rounded">
                  {getLabel(whitelabelConfig, 'exclusiveContentLabel', 'Exclusive Content')}
                </div>
              </div>
            )}
            {notebook?.visibility === 'fans' && !isAuthenticated && (
              <div className="absolute top-2 left-2 z-50">
                <div className="backdrop-blur-md bg-white/60 border border-gray-300/40 shadow-sm text-gray-900 text-[10px] md:text-xs font-semibold px-2.5 py-0.5 rounded">
                  Free Access
                </div>
              </div>
            )}
            <>
              {notebook?.banner_url && (
                <img
                  src={notebook?.banner_url}
                  alt="banner"
                  className="absolute inset-0 object-cover w-full h-[170px] blur-md"
                />
              )}
              {notebook?.banner_url && (
                <img
                  src={notebook?.banner_url}
                  alt="banner"
                  className="relative w-full h-[180px] object-contain z-40"
                />
              )}
              {notebook?.children_count != null && notebook?.children_count > 0 && (
                <div className="absolute bottom-2 left-3 z-50 flex items-center gap-2">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full backdrop-blur-md bg-white/30 border border-white/40 shadow-lg">
                    <Folder className="w-5 h-5 text-gray-800" />
                  </div>
                  <div className="backdrop-blur-md bg-white/30 border border-white/40 shadow-lg rounded-full px-3 py-1.5">
                    <Text type="xs" className="text-gray-800 font-semibold">
                      {notebook?.children_count} {notebook?.children_count > 1 ? 'items' : 'item'}
                    </Text>
                  </div>
                </div>
              )}
            </>
          </div>

          {userType === 'fan' && shouldShowLockedContentWording && (
            <div className="absolute bg-black/50 top-0 right-0 w-full h-full rounded-t-md flex items-center justify-center flex-col space-y-1 z-40">
              <div className="mt-4 flex items-center bg-white border max-w-[200px] rounded-2xl py-2 text-sm space-x-1 px-6 font-semibold">
                <Icon type="lock" stroke="#000" />
                <span>Unlock content</span>
              </div>
              <div className="text-xs text-white text-center">Available by subscription</div>
            </div>
          )}
        </div>

        {userType === 'creator' && (
          <ContentItemDeleteModal
            open={isDeleteModalOpen}
            setOpen={setDeleteModalOpen}
            notebookId={notebookToDelete}
            state={state!}
          />
        )}

        <div className=" flex flex-col justify-center pl-4 w-full rounded-bl-md rounded-br-md">
          <div className="flex items-center justify-between pt-4">
            <Text
              type="m"
              className="text-base font-semibold text-gray-800 w-full max-w-[90%] lg:max-w-[80%] xl:max-w-[75%] pr-2 overflow-hidden text-ellipsis whitespace-nowrap">
              {notebook?.name}
            </Text>

            <ContentItemMenu
              notebook={notebook}
              userType={userType}
              isOpen={dropdownOpen}
              onOpenChange={setDropdownOpen}
              onEdit={() => {}}
              onDelete={(e) => handleDelete(e)}
              onPin={{ handlePin, pinned: !!notebook?.pinned_at }}
              onMove={(e) => handleMove(e)}
            />
          </div>

          <Text
            type="m"
            className="text-note text-xs line-clamp-2 w-full pr-2 my-1 lg:max-w-[275px] max-w-[200px]">
            {notebook?.description}
          </Text>

          {shouldShowPublishDate && (
            <div className="flex items-center">
              <Note type="xs" className="text-xs font-light">
                {notebook?.date_of_publish
                  ? 'Published'
                  : userType === 'creator'
                  ? 'Draft, created'
                  : 'Created'}{' '}
                {dayjs(dynamicDate).format('DD MMM')}
              </Note>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
};
